import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Loader2, X, ExternalLink } from "lucide-react";
import { STATUS_META, CERT_META, Badge, CertReasons, fmt } from "@/pages/admin/StudentsOverview";

const TASK_STATUS = { done: "منجزة", open: "مفتوحة", submitted: "مُسلَّمة" };
const ATT_STATUS = { present: "حاضر", remote: "عن بُعد", absent: "غائب", late: "متأخر", excused: "بعذر" };
const ROLE = { association: "الجمعية", mentor: "الموجّه", student: "الطالب" };

function Section({ title, count, children, testId }) {
  return (
    <section data-testid={testId}>
      <h3 className="text-sm font-black text-slate-900 mb-2">{title} <span className="text-slate-400 arabic-numerals">({count})</span></h3>
      {count ? <div className="space-y-2">{children}</div> : <div className="text-xs text-slate-500 rounded-lg bg-slate-50 p-3">لا توجد بيانات.</div>}
    </section>
  );
}

function Body({ d }) {
  return (
    <div className="space-y-5 arabic-numerals">
      {d.association_notes && <div className="rounded-xl bg-teal-50 border border-teal-100 p-3 text-xs text-teal-900 leading-6"><b>ملاحظة الجمعية:</b> {d.association_notes}</div>}
      <Section title="المهام" count={d.tasks.length} testId="detail-tasks">
        {d.tasks.map((t) => (
          <div key={t.id} className="rounded-xl border border-slate-200 p-3 text-sm">
            <div className="flex justify-between gap-2"><b className="text-slate-900">{t.title}</b><span className="text-[11px] text-slate-500 shrink-0">{TASK_STATUS[t.status] || t.status}</span></div>
            <div className="mt-1 text-xs text-slate-600">درجة الجمعية: {fmt(t.score)} · درجة الموجّه: {fmt(t.mentor_score)}{t.due_date ? ` · التسليم: ${new Date(t.due_date).toLocaleDateString("ar-SA")}` : ""}</div>
          </div>
        ))}
      </Section>
      <Section title="التقييمات الشهرية" count={d.evaluations.length} testId="detail-evaluations">
        {d.evaluations.map((e) => (
          <div key={e.id} className="rounded-xl border border-slate-200 p-3 text-sm">
            <div className="flex justify-between gap-2"><b className="text-slate-900">{e.form_title}</b><span className="font-black text-teal-800">{fmt(e.average_score, " / 5")}</span></div>
            <div className="mt-1 text-xs text-slate-600">{e.period_label} · المقيِّم: {ROLE[e.evaluator_role] || e.evaluator_role} {e.evaluator_name ? `(${e.evaluator_name})` : ""}</div>
          </div>
        ))}
      </Section>
      <Section title="سجل الحضور" count={d.attendance.length} testId="detail-attendance">
        {d.attendance.map((a) => (
          <div key={a.id} className="flex justify-between rounded-lg bg-slate-50 px-3 py-2 text-xs"><span>{a.date}</span><span>{ATT_STATUS[a.status] || a.status}</span><span>{fmt(a.hours, " ساعة")}</span></div>
        ))}
      </Section>
    </div>
  );
}

export default function StudentOverviewDetail({ row, onClose }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (!row.application_id) { return; }
    api.get(`/admin/students-overview/${row.application_id}`).then(({ data }) => setD(data)).catch((e) => setErr(formatApiError(e)));
  }, [row.application_id]);

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center sm:px-4" onClick={onClose} data-testid="student-detail-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-2xl rounded-t-3xl sm:rounded-2xl bg-white shadow-2xl flex flex-col animate-sheet-up sm:animate-fade-in-fast" style={{ maxHeight: "92dvh" }}>
        <div className="flex items-start justify-between p-5 border-b border-slate-100">
          <div className="min-w-0">
            <h2 className="text-lg font-heading font-black text-slate-900">{row.student_name}</h2>
            <div className="text-xs text-slate-500 truncate" dir="ltr">{row.student_email}</div>
            <div className="mt-1 text-xs text-teal-800 truncate">{row.opportunity_title || "—"} · {row.association_name || "—"}</div>
            <div className="mt-2 flex gap-1.5 flex-wrap"><Badge meta={STATUS_META[row.status]} /><Badge meta={CERT_META[row.certificate_status]} testId="detail-cert-status" /></div>
            <CertReasons r={row} testId="detail-cert-reasons" />
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 min-h-11 min-w-11 grid place-items-center" data-testid="student-detail-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto">
          {!row.application_id ? <div className="text-sm text-slate-500" data-testid="detail-no-application">هذا الطالب لم يتقدم لأي فرصة بعد.</div>
            : err ? <div className="text-sm text-rose-700">{err}</div>
            : !d ? <div className="grid place-items-center py-10"><Loader2 className="h-6 w-6 animate-spin text-teal-700" /></div>
            : <Body d={d} />}
        </div>
        {row.application_id && (
          <div className="p-4 border-t border-slate-100 bg-slate-50 rounded-b-2xl">
            <Link to={`/dashboard/admin/journeys/${row.application_id}`} className="inline-flex w-full items-center justify-center gap-2 min-h-11 rounded-xl bg-slate-900 text-white text-sm font-bold" data-testid="detail-open-journey"><ExternalLink className="h-4 w-4" /> فتح رحلة الطالب كاملة</Link>
          </div>
        )}
      </div>
    </div>
  );
}
